// src/components/Global/CtaLink/index.tsx
import React, { useContext, useMemo } from 'react';
import PrivacyButton, { isKetchLink, cleanCountryLink } from '../PrivacyButton';
import { PageMetadataContext } from '../../../templates/PageMetaDataContext';
import { prefixWithLang } from '../../../global/countries';
import * as styles from './CtaLink.module.css';

interface Props {
    href?: string;
    target?: string;
    color?: string;
    className?: string;
    borderColor?: string;
    backgroundColor?: string;
    hoverBorderColor?: string;
    hoverFontColor?: string;
    hoverBgColor?: string;
    visualStyle?: 'outlineDark' | 'outlineLight';
    id?: string;
    ariaLabel?: string;
    onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
    children: React.ReactNode;
    elementCodename?: string;
}

const CtaLink: React.FC<Props> = ({
    href,
    target,
    color,
    className,
    borderColor,
    backgroundColor,
    hoverBorderColor,
    hoverFontColor,
    hoverBgColor,
    visualStyle = 'outlineDark',
    children,
    id,
    ariaLabel,
    onClick,
    elementCodename = 'cta_configuration__cta_label',
}) => {
    const pageMetadata = useContext(PageMetadataContext);
    const preferredLanguage = pageMetadata?.preferredLanguage?.toLowerCase();

    const hoverBorderColorHex = hoverBorderColor || '#f06623';
    const hoverFontColorHex = hoverFontColor || '#ffffff';
    const hoverBgColorHex = hoverBgColor || '#f06623';

    const languageSpecificHref = useMemo(() => {
        if (!href) return undefined;
        return prefixWithLang(cleanCountryLink(href), preferredLanguage);
    }, [href, preferredLanguage]);

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        onClick?.(e);

        if (e.defaultPrevented) return;

        if (
            typeof window !== 'undefined' &&
            languageSpecificHref &&
            languageSpecificHref.includes('#') &&
            !languageSpecificHref.startsWith('http') &&
            !target
        ) {
            const hashIndex = languageSpecificHref.indexOf('#');
            const pathPart =
                hashIndex > 0
                    ? languageSpecificHref.substring(0, hashIndex)
                    : '';

            const hash = languageSpecificHref.substring(hashIndex);
            const currentPath = window.location.pathname;

            const normalizedCurrentPath = currentPath.replace(/\/$/, '');
            const normalizedLinkPath = pathPart.replace(/\/$/, '');

            if (
                (normalizedCurrentPath === normalizedLinkPath ||
                    normalizedCurrentPath === normalizedLinkPath + '/' ||
                    normalizedLinkPath === '' ||
                    normalizedLinkPath === '/') &&
                hash
            ) {
                e.preventDefault();

                const anchorId = hash.substring(1);

                const newUrl = `${currentPath}${window.location.search}${hash}`;
                window.history.replaceState(null, '', newUrl);

                requestAnimationFrame(() => {
                    setTimeout(() => {
                        const targetElement = document.getElementById(anchorId);

                        if (targetElement) {
                            const offset = 100;
                            const top =
                                targetElement.getBoundingClientRect().top +
                                window.scrollY -
                                offset;

                            window.scrollTo({
                                top,
                                behavior: 'smooth',
                            });
                        }
                    }, 50);
                });
            }
        }
    };

    // using CSS variables instead of inline backgroundColor/borderColor/color
    const inlineStyle: React.CSSProperties & Record<string, string | number> = {
        ...(color && { '--cta-color': color }),
        ...(borderColor
            ? { '--cta-border': borderColor }
            : color
            ? { '--cta-border': color }
            : {}),
        ...(backgroundColor && { '--cta-bg': backgroundColor }),

        '--cta-hover-border': hoverBorderColorHex,
        '--cta-hover-bg': hoverBgColorHex,
        '--cta-hover-color': hoverFontColorHex,
    };

    if (href && isKetchLink(href)) {
        return (
            <PrivacyButton
                className={`${styles.ctaLink} ${styles[visualStyle]} ${
                    className || ''
                }`}
            >
                {children}
            </PrivacyButton>
        );
    }

    return (
        <a
            rel={
                [
                    target === '_blank' || target === '_new'
                        ? 'noopener noreferrer'
                        : '',
                    languageSpecificHref?.endsWith('.pdf') ? 'nofollow' : '',
                ]
                    .filter(Boolean)
                    .join(' ') || undefined
            }
            id={id}
            href={languageSpecificHref}
            target={target === '_new' ? '_blank' : target}
            aria-label={ariaLabel}
            onClick={handleClick}
            className={`${styles.ctaLink} ${styles[visualStyle]} ${
                className || ''
            }`}
            style={inlineStyle}
            {...(elementCodename
                ? { 'data-kontent-element-codename': elementCodename }
                : {})}
        >
            {children}
        </a>
    );
};

export default CtaLink;
